// src/pages/inventory/rimender-stock/RimenderStock.jsx
import React, { useState, useEffect } from "react";
import {
    Typography,
    Box,
    Table,
    Paper,
    CircularProgress,
    Alert,
    TablePagination,
    Stack,
    Snackbar,
    TextField,
    IconButton,
    Popover,
    FormControlLabel,
    Checkbox,
    Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';

import { useTheme } from "@mui/material/styles";
import "../../../components/css/ded-style.css";
import useAuthCheck from "../../../hooks/useAuthCheck";
import { formatNumber, formatText } from "../../../utils/formatters-master";
import CustomTableContainer from "../../../components/CustomScrollContainer";

// Import service functions
import {
    convertDateForComparison,
    formatMonthYearHeader,
    fetchMaterialMaster,
    fetchUomData,
    fetchMaterialCategory,
    fetchForecastData,
    fetchActualData,
    fetchStockData
} from "./rimenderStockService";

// Import table components
import RimenderStockTableHead from "./RimenderStockTableHead";
import RimenderStockTableBody from "./RimenderStockTableBody";

function RimenderStock() {
    const { user, loading: authLoading } = useAuthCheck();
    const theme = useTheme();

    const [dataList, setDataList] = useState([]);
    const [filteredDataList, setFilteredDataList] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });
    const [searchQuery, setSearchQuery] = useState("");

    // State untuk filter
    const [filterAnchorEl, setFilterAnchorEl] = useState(null);
    const [showFilter, setShowFilter] = useState(false);
    const [selectedStatuses, setSelectedStatuses] = useState(['Warning']); // Default Warning

    // State untuk data referensi
    const [materialMasterList, setMaterialMasterList] = useState([]);
    const [uomList, setUomList] = useState([]);
    const [materialCategoryList, setMaterialCategoryList] = useState([]);

    // State untuk data forecast, actual, dan stock
    const [forecastData, setForecastData] = useState({});
    const [actualData, setActualData] = useState({});
    const [stockData, setStockData] = useState({});

    // State untuk header dinamis
    const [forecastHeader, setForecastHeader] = useState("");
    const [actualHeader, setActualHeader] = useState("");

    // Fungsi untuk mendapatkan nama kategori material
    const getNamaKategoriMaterial = (idMaterial) => {
        const category = materialCategoryList.find((item) => item.id_material === idMaterial);
        return category ? category.nama_kategori_material : "";
    };

    // Fungsi untuk mendapatkan nama satuan
    const getNamaSatuan = (idSatuan) => {
        const uom = uomList.find((item) => item.id_satuan === idSatuan);
        return uom ? uom.nama_satuan : "";
    };

    // Ambil data terbaru dari forecast berdasarkan kode material
    const getLatestForecast = (kodeMaterial) => {
        if (!forecastData[kodeMaterial]) return null;

        // Ambil semua tanggal dan cari yang terbaru
        const dates = Object.keys(forecastData[kodeMaterial]);
        if (dates.length === 0) return null;

        // Sort dates descending (terbaru di atas) dengan konversi ke YYYY-MM-DD
        dates.sort((a, b) => {
            const dateA = convertDateForComparison(a);
            const dateB = convertDateForComparison(b);
            return dateB.localeCompare(dateA); // descending
        });

        const latestDate = dates[0];
        return forecastData[kodeMaterial][latestDate];
    };

    // Ambil data terbaru dari actual berdasarkan kode material
    const getLatestActual = (kodeMaterial) => {
        if (!actualData[kodeMaterial]) return null;

        // Ambil semua tanggal dan cari yang terbaru
        const dates = Object.keys(actualData[kodeMaterial]);
        if (dates.length === 0) return null;

        // Sort dates descending (terbaru di atas) dengan konversi ke YYYY-MM-DD
        dates.sort((a, b) => {
            const dateA = convertDateForComparison(a);
            const dateB = convertDateForComparison(b);
            return dateB.localeCompare(dateA); // descending
        });

        const latestDate = dates[0];
        return actualData[kodeMaterial][latestDate];
    };

    // Ambil data terbaru dari stock berdasarkan kode material
    const getLatestStock = (kodeMaterial) => {
        if (!stockData[kodeMaterial]) return null;

        // Ambil semua tanggal dan cari yang terbaru
        const dates = Object.keys(stockData[kodeMaterial]);
        if (dates.length === 0) return null;

        // Sort dates descending (terbaru di atas) dengan konversi ke YYYY-MM-DD
        dates.sort((a, b) => {
            const dateA = convertDateForComparison(a);
            const dateB = convertDateForComparison(b);
            return dateB.localeCompare(dateA); // descending
        });

        const latestDate = dates[0];
        return stockData[kodeMaterial][latestDate];
    };

    // Update header berdasarkan tanggal terbaru dari semua data
    useEffect(() => {
        if (Object.keys(forecastData).length > 0 || Object.keys(actualData).length > 0 || Object.keys(stockData).length > 0) {
            // Kumpulkan semua tanggal dari forecast, actual, dan stock
            const allDates = [];

            Object.keys(forecastData).forEach((kodeMaterial) => {
                Object.keys(forecastData[kodeMaterial] || {}).forEach((tanggal) => {
                    if (!allDates.includes(tanggal)) {
                        allDates.push(tanggal);
                    }
                });
            });

            Object.keys(actualData).forEach((kodeMaterial) => {
                Object.keys(actualData[kodeMaterial] || {}).forEach((tanggal) => {
                    if (!allDates.includes(tanggal)) {
                        allDates.push(tanggal);
                    }
                });
            });

            Object.keys(stockData).forEach((kodeMaterial) => {
                Object.keys(stockData[kodeMaterial] || {}).forEach((tanggal) => {
                    if (!allDates.includes(tanggal)) {
                        allDates.push(tanggal);
                    }
                });
            });

            if (allDates.length > 0) {
                // Cari tanggal terbaru
                allDates.sort((a, b) => {
                    const dateA = convertDateForComparison(a);
                    const dateB = convertDateForComparison(b);
                    return dateB.localeCompare(dateA);
                });

                const latestDate = allDates[0];
                const formattedDate = formatMonthYearHeader(latestDate);

                // Simpan hanya format tanggalnya saja, tanpa teks
                setForecastHeader(formattedDate);
                setActualHeader(formattedDate);
            }
        }
    }, [forecastData, actualData, stockData]);

    useEffect(() => {
        const fetchAllData = async () => {
            setLoadingData(true);
            try {
                // Fetch semua data yang diperlukan secara paralel
                const [
                    materialMasterResult,
                    uomResult,
                    materialCategoryResult,
                    forecastResult,
                    actualResult,
                    stockResult
                ] = await Promise.all([
                    fetchMaterialMaster(),
                    fetchUomData(),
                    fetchMaterialCategory(),
                    fetchForecastData(),
                    fetchActualData(),
                    fetchStockData(),
                ]);

                setMaterialMasterList(materialMasterResult);
                setUomList(uomResult);
                setMaterialCategoryList(materialCategoryResult);
                setForecastData(forecastResult);
                setActualData(actualResult);
                setStockData(stockResult);

            } catch (error) {
                console.error("Error fetching rimender stock data:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal memuat data rimender stock.",
                    severity: "error",
                });
            } finally {
                setLoadingData(false);
            }
        };

        if (user) {
            fetchAllData();
        }
    }, [user]);

    // Gabungkan semua data untuk ditampilkan di tabel
    useEffect(() => {
        if (materialMasterList.length > 0) {
            const combinedData = materialMasterList.map((item) => {
                const latestForecast = getLatestForecast(item.kode_material);
                const latestActual = getLatestActual(item.kode_material);
                const latestStock = getLatestStock(item.kode_material);

                // Parse nilai forecast untuk Fresh Pineapple (M1-M6)
                const forecastFP_M1 = latestForecast ? parseFloat(latestForecast.fresh_pineapple_M1) || 0 : 0;
                const forecastFP_M2 = latestForecast ? parseFloat(latestForecast.fresh_pineapple_M2) || 0 : 0;
                const forecastFP_M3 = latestForecast ? parseFloat(latestForecast.fresh_pineapple_M3) || 0 : 0;
                const forecastFP_M4 = latestForecast ? parseFloat(latestForecast.fresh_pineapple_M4) || 0 : 0;
                const forecastFP_M5 = latestForecast ? parseFloat(latestForecast.fresh_pineapple_M5) || 0 : 0;
                const forecastFP_M6 = latestForecast ? parseFloat(latestForecast.fresh_pineapple_M6) || 0 : 0;

                // Parse nilai forecast untuk Banana (M1-M6)
                const forecastBanana_M1 = latestForecast ? parseFloat(latestForecast.banana_M1) || 0 : 0;
                const forecastBanana_M2 = latestForecast ? parseFloat(latestForecast.banana_M2) || 0 : 0;
                const forecastBanana_M3 = latestForecast ? parseFloat(latestForecast.banana_M3) || 0 : 0;
                const forecastBanana_M4 = latestForecast ? parseFloat(latestForecast.banana_M4) || 0 : 0;
                const forecastBanana_M5 = latestForecast ? parseFloat(latestForecast.banana_M5) || 0 : 0;
                const forecastBanana_M6 = latestForecast ? parseFloat(latestForecast.banana_M6) || 0 : 0;

                // Parse nilai forecast untuk Other Fresh Fruits (M1-M3)
                const forecastOther_M1 = latestForecast ? parseFloat(latestForecast.other_fresh_fruits_M1) || 0 : 0;
                const forecastOther_M2 = latestForecast ? parseFloat(latestForecast.other_fresh_fruits_M2) || 0 : 0;
                const forecastOther_M3 = latestForecast ? parseFloat(latestForecast.other_fresh_fruits_M3) || 0 : 0;

                // Hitung Total Forecast
                const totalForecastFP = forecastFP_M1 + forecastFP_M2 + forecastFP_M3 + forecastFP_M4 + forecastFP_M5 + forecastFP_M6;
                const totalForecastBanana = forecastBanana_M1 + forecastBanana_M2 + forecastBanana_M3 + forecastBanana_M4 + forecastBanana_M5 + forecastBanana_M6;
                const totalForecastOther = forecastOther_M1 + forecastOther_M2 + forecastOther_M3;

                // Parse nilai actual
                const actualFP = latestActual ? parseFloat(latestActual.fresh_pineapple) || 0 : 0;
                const actualBanana = latestActual ? parseFloat(latestActual.banana) || 0 : 0;
                const actualOther = latestActual ? parseFloat(latestActual.other_fresh_fruits) || 0 : 0;

                // Hitung FC Berjalan
                const fcBerjalanFP = forecastFP_M1 - actualFP;
                const fcBerjalanBanana = forecastBanana_M1 - actualBanana;
                const fcBerjalanOther = forecastOther_M1 - actualOther;

                // Hitung % Realisasi
                const persenRealisasiFP = forecastFP_M1 > 0 ? ((actualFP / forecastFP_M1) * 100).toFixed(0) : 0;
                const persenRealisasiBanana = forecastBanana_M1 > 0 ? ((actualBanana / forecastBanana_M1) * 100).toFixed(0) : 0;
                const persenRealisasiOther = forecastOther_M1 > 0 ? ((actualOther / forecastOther_M1) * 100).toFixed(0) : 0;

                // Parse nilai stock
                const stockWarehouseBpp = latestStock ? parseFloat(latestStock.warehouse_bpp) || 0 : 0;
                const stockGdMixerFreshPine = latestStock ? parseFloat(latestStock.gd_mixer_fresh_pine) || 0 : 0;
                const stockGdMatBanana = latestStock ? parseFloat(latestStock.gd_mat_banana) || 0 : 0;
                const stockGdMatOtherFruits = latestStock ? parseFloat(latestStock.gd_mat_other_fruits) || 0 : 0;
                const stockQualityInsp = latestStock ? parseFloat(latestStock.quality_insp) || 0 : 0;
                const stockTransitAndTransfer = latestStock ? parseFloat(latestStock.transit_and_transfer) || 0 : 0;

                // Hitung total forecast dan total realisasi (untuk semua kategori) - menggunakan M1 untuk perhitungan total
                const totalForecast = forecastFP_M1 + forecastBanana_M1 + forecastOther_M1;
                const totalRealisasi = actualFP + actualBanana + actualOther;

                // Hitung FC Berjalan (total)
                const fcBerjalan = totalForecast - totalRealisasi;

                // Hitung Total Stock PG4
                const totalStockPG4 = stockWarehouseBpp + stockGdMixerFreshPine + stockGdMatBanana + 
                                      stockGdMatOtherFruits + stockQualityInsp + stockTransitAndTransfer;

                // Hitung Kekurangan terhadap Stock
                const kekuranganTerhadapStock = totalStockPG4 - fcBerjalan;

                // Hitung Safety Stock (20% dari Total Forecast)
                const safetyStock = totalForecast * 0.2;

                // Hitung Estimasi Kekurangan Stock Bulan Berjalan (M1)
                // Formula: Stock PG4 - Total Stock PG4 - (fcBerjalanFP + fcBerjalanBanana + fcBerjalanOther)
                const estimasiKekuranganM1 = totalStockPG4 - (fcBerjalanFP + fcBerjalanBanana + fcBerjalanOther);

                // Hitung Estimasi Kekurangan Stock terhadap Forecast (M2)
                // Formula: IF(estimasiKekuranganM1 < 0; -forecastFP_M2 - forecastOther_M2 - forecastBanana_M2; estimasiKekuranganM1 - forecastFP_M2 - forecastOther_M2 - forecastBanana_M2)
                const totalForecastM2 = forecastFP_M2 + forecastOther_M2 + forecastBanana_M2;
                const estimasiKekuranganM2 = estimasiKekuranganM1 < 0 
                    ? -totalForecastM2 
                    : estimasiKekuranganM1 - totalForecastM2;

                // Hitung Estimasi Kekurangan Stock terhadap Forecast (M3)
                // Formula: IF(estimasiKekuranganM2 < 0; -forecastFP_M3 - forecastOther_M3 - forecastBanana_M3; estimasiKekuranganM2 - forecastFP_M3 - forecastOther_M3 - forecastBanana_M3)
                const totalForecastM3 = forecastFP_M3 + forecastOther_M3 + forecastBanana_M3;
                const estimasiKekuranganM3 = estimasiKekuranganM2 < 0 
                    ? -totalForecastM3 
                    : estimasiKekuranganM2 - totalForecastM3;

                // Hitung Status Stock terhadap Fc M1
                // Formula: 
                // - jika totalForecast kosong/null/0, tampilkan "-"
                // - jika stockWarehouseBpp > totalForecast, tampilkan "Aman"
                // - jika stockWarehouseBpp < totalForecast, tampilkan "Warning"
                let statusStockTerhadapFcM1 = "-";
                if (totalForecast && totalForecast !== 0) {
                    if (stockWarehouseBpp > totalForecast) {
                        statusStockTerhadapFcM1 = "Aman";
                    } else if (stockWarehouseBpp < totalForecast) {
                        statusStockTerhadapFcM1 = "Warning";
                    }
                }

                return {
                    ...item,
                    kategori_material: getNamaKategoriMaterial(item.id_material),
                    uom: getNamaSatuan(item.id_satuan),

                    // Forecast values - Fresh Pineapple (M1-M6 dan Total)
                    forecast_fresh_pineapple_M1: forecastFP_M1 || "",
                    forecast_fresh_pineapple_M2: forecastFP_M2 || "",
                    forecast_fresh_pineapple_M3: forecastFP_M3 || "",
                    forecast_fresh_pineapple_M4: forecastFP_M4 || "",
                    forecast_fresh_pineapple_M5: forecastFP_M5 || "",
                    forecast_fresh_pineapple_M6: forecastFP_M6 || "",
                    forecast_fresh_pineapple_total: totalForecastFP || "",

                    // Forecast values - Banana (M1-M6 dan Total)
                    forecast_banana_M1: forecastBanana_M1 || "",
                    forecast_banana_M2: forecastBanana_M2 || "",
                    forecast_banana_M3: forecastBanana_M3 || "",
                    forecast_banana_M4: forecastBanana_M4 || "",
                    forecast_banana_M5: forecastBanana_M5 || "",
                    forecast_banana_M6: forecastBanana_M6 || "",
                    forecast_banana_total: totalForecastBanana || "",

                    // Forecast values - Other Fresh Fruits (M1-M3 dan Total)
                    forecast_other_M1: forecastOther_M1 || "",
                    forecast_other_M2: forecastOther_M2 || "",
                    forecast_other_M3: forecastOther_M3 || "",
                    forecast_other_total: totalForecastOther || "",

                    // Actual values
                    actual_fresh_pineapple: actualFP || "",
                    actual_banana: actualBanana || "",
                    actual_other_fresh_fruits: actualOther || "",

                    // FC Berjalan
                    fc_berjalan_fresh_pineapple: fcBerjalanFP,
                    fc_berjalan_banana: fcBerjalanBanana,
                    fc_berjalan_other: fcBerjalanOther,

                    // % Realisasi
                    persen_realisasi_fresh_pineapple: persenRealisasiFP,
                    persen_realisasi_banana: persenRealisasiBanana,
                    persen_realisasi_other: persenRealisasiOther,

                    // Stock PG4 values
                    stock_warehouse_bpp: stockWarehouseBpp || "",
                    stock_gd_mixer_fresh_pine: stockGdMixerFreshPine || "",
                    stock_gd_mat_banana: stockGdMatBanana || "",
                    stock_gd_mat_other_fruits: stockGdMatOtherFruits || "",
                    stock_quality_insp: stockQualityInsp || "",
                    stock_transit_and_transfer: stockTransitAndTransfer || "",
                    total_stock_pg4: totalStockPG4,

                    // Total dan perhitungan
                    total_forecast: totalForecast,
                    total_realisasi: totalRealisasi,
                    fc_berjalan: fcBerjalan,
                    kekurangan_terhadap_stock: kekuranganTerhadapStock,
                    
                    // Kolom baru
                    safety_stock: safetyStock,

                    // Estimasi Kekurangan
                    estimasi_kekurangan_m1: estimasiKekuranganM1,
                    estimasi_kekurangan_m2: estimasiKekuranganM2,
                    estimasi_kekurangan_m3: estimasiKekuranganM3,

                    // Status Stock terhadap Fc M1
                    status_stock_terhadap_fc_m1: statusStockTerhadapFcM1,

                    // Simpan tanggal terbaru untuk referensi
                    latest_forecast_date: latestForecast ? latestForecast.tanggal_update : "",
                    latest_actual_date: latestActual ? latestActual.tanggal_update : "",
                    latest_stock_date: latestStock ? latestStock.tanggal_update : "",
                };
            });

            // Sort by index
            combinedData.sort((a, b) => {
                const indexA = a.index ? parseInt(a.index) : 0;
                const indexB = b.index ? parseInt(b.index) : 0;
                return indexA - indexB;
            });

            setDataList(combinedData);
        }
    }, [materialMasterList, forecastData, actualData, stockData]);

    // Effect untuk melakukan filter ketika dataList atau selectedStatuses berubah
    useEffect(() => {
        if (dataList.length > 0) {
            // Jika tidak ada status yang dipilih, tampilkan semua data
            if (selectedStatuses.length === 0) {
                setFilteredDataList(dataList);
            } else {
                // Filter data berdasarkan status yang dipilih
                const filtered = dataList.filter(item => 
                    selectedStatuses.includes(item.status_stock_terhadap_fc_m1)
                );
                setFilteredDataList(filtered);
            }
            setPage(0); // Reset ke halaman pertama setelah filter
        } else {
            setFilteredDataList([]);
        }
    }, [dataList, selectedStatuses]);

    // Handle search
    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
        setPage(0);
    };

    // Filter data berdasarkan pencarian dan status filter
    const finalFilteredDataList = filteredDataList.filter((item) => {
        if (!searchQuery) return true;

        const query = searchQuery.toLowerCase();
        return (
            (item.kategori_material && item.kategori_material.toLowerCase().includes(query)) ||
            (item.kode_material && item.kode_material.toLowerCase().includes(query)) ||
            (item.nama_produk && item.nama_produk.toLowerCase().includes(query))
        );
    });

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleRefresh = () => {
        setLoadingData(true);
        Promise.all([
            fetchMaterialMaster(),
            fetchUomData(),
            fetchMaterialCategory(),
            fetchForecastData(),
            fetchActualData(),
            fetchStockData(),
        ]).then(([
            materialMasterResult,
            uomResult,
            materialCategoryResult,
            forecastResult,
            actualResult,
            stockResult
        ]) => {
            setMaterialMasterList(materialMasterResult);
            setUomList(uomResult);
            setMaterialCategoryList(materialCategoryResult);
            setForecastData(forecastResult);
            setActualData(actualResult);
            setStockData(stockResult);
            setLoadingData(false);
            setSnackbar({
                open: true,
                message: "Data berhasil direfresh.",
                severity: "success",
            });
        }).catch((error) => {
            console.error("Error refreshing data:", error);
            setLoadingData(false);
            setSnackbar({
                open: true,
                message: "Gagal merefresh data.",
                severity: "error",
            });
        });
    };

    // Handler untuk filter
    const handleFilterClick = (event) => {
        setFilterAnchorEl(event.currentTarget);
        setShowFilter(!showFilter);
    };

    const handleFilterClose = () => {
        setFilterAnchorEl(null);
        setShowFilter(false);
    };

    const handleStatusChange = (status) => {
        setSelectedStatuses(prev => {
            if (prev.includes(status)) {
                return prev.filter(s => s !== status);
            } else {
                return [...prev, status];
            }
        });
    };

    // Dapatkan unique status dari data
    const uniqueStatuses = [...new Set(dataList.map(item => item.status_stock_terhadap_fc_m1))].filter(status => status !== "-");

    const filterOpen = Boolean(filterAnchorEl);

    if (authLoading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">Anda harus login untuk mengakses halaman ini</Alert>
            </Box>
        );
    }

    return (
        <Paper sx={{ width: "100%", overflow: "hidden", p: 0, mb: 0, boxShadow:0 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography variant="h6" component="h2">
                    Reminder Stock
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                        size="small"
                        placeholder="Cari Group, Item Code, atau Description..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                        InputProps={{
                            startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
                        }}
                        sx={{ width: 300 }}
                    />
                    <IconButton color="primary" size="small" onClick={handleRefresh} disabled={loadingData}>
                        {loadingData ? <CircularProgress size={20} /> : <RefreshIcon />}
                    </IconButton>
                    <IconButton 
                        color="primary" 
                        size="small" 
                        onClick={handleFilterClick}
                        disabled={loadingData}
                    >
                        {showFilter ? <FilterAltOutlinedIcon /> : <FilterAltOffOutlinedIcon />}
                    </IconButton>

{/* Bagian Popover - dengan checkbox vertical */}
<Popover
    open={filterOpen}
    anchorEl={filterAnchorEl}
    onClose={handleFilterClose}
    anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
    }}
    transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
    }}
>
    <Box sx={{ p: 2, minWidth: 200 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Filter Status Stock
        </Typography>
        
        {/* Checkbox dalam susunan vertical menggunakan Stack */}
        <Stack direction="column" spacing={0.5}>
            {uniqueStatuses.map((status) => (
                <FormControlLabel
                    key={status}
                    control={
                        <Checkbox
                            checked={selectedStatuses.includes(status)}
                            onChange={() => handleStatusChange(status)}
                            size="small"
                        />
                    }
                    label={status}
                    sx={{ 
                        ml: 0, // Menghilangkan margin left default
                        '& .MuiFormControlLabel-label': { 
                            fontSize: '0.9rem' 
                        } 
                    }}
                />
            ))}
        </Stack>
        
        {uniqueStatuses.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Tidak ada status untuk difilter
            </Typography>
        )}
        
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
            <Button 
                size="small" 
                onClick={() => setSelectedStatuses([])}
            >
                Reset
            </Button>
            <Button 
                size="small" 
                variant="contained" 
                onClick={handleFilterClose}
            >
                OK
            </Button>
        </Box>
    </Box>
</Popover>

                </Stack>
            </Stack>

            {loadingData ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
                    <CircularProgress />
                </Box>
            ) : finalFilteredDataList.length > 0 ? (
                <>
                    <CustomTableContainer
                        sx={{
                            maxHeight: 500,
                            border: "1px solid #e0e0e0",
                            borderRadius: 1,
                        }}
                        hoverScrollbar={true}
                        scrollbarHeight="8px"
                        scrollbarWidth="8px"
                        scrollbarThumbColor="#c1c1c1"
                        scrollbarHoverColor="#a8a8a8"
                    >
                        <Table stickyHeader aria-label="rimender stock table">
                            <RimenderStockTableHead 
                                forecastHeader={forecastHeader} 
                                actualHeader={actualHeader} 
                            />
                            <RimenderStockTableBody 
                                data={finalFilteredDataList} 
                                page={page} 
                                rowsPerPage={rowsPerPage} 
                            />
                        </Table>
                    </CustomTableContainer>

                    {/* Container untuk TablePagination */}
                    <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mt: 1 }}>
                        <TablePagination
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            component="div"
                            count={finalFilteredDataList.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            sx={{
                                flexShrink: 0,
                                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                                    margin: 0,
                                },
                            }}
                        />
                    </Box>
                </>
            ) : (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: 200,
                        border: "1px dashed #e0e0e0",
                        borderRadius: 1,
                    }}
                >
                    <Typography variant="body1" color="text.secondary">
                        {searchQuery ? "Tidak ada data yang sesuai dengan pencarian" : 
                         selectedStatuses.length > 0 ? "Tidak ada data dengan status yang dipilih" : 
                         "Tidak ada data rimender stock."}
                    </Typography>
                </Box>
            )}
            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Paper>
    );
}

export default RimenderStock;